import { generateDateKeys, normalizeDateKey } from "./kpiPerDayUtils";

const toNonNegativeNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : 0;
};

const KPI_BRANCH_NAMES = {
  1: "Đà Nẵng",
  2: "Hồ Chí Minh",
  3: "Bonpas",
};

export const formatKpiDate = (value) => {
  const dateKey = normalizeDateKey(value);
  if (!dateKey) return "Không xác định";
  const [year, month, day] = dateKey.split("-");
  return `${day}/${month}/${year}`;
};

export const normalizeSeasonList = (payload) => {
  const rows = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
    : [];

  return rows
    .map((item) => {
      const startDate = normalizeDateKey(
        item.KPI_NgayBatDau ||
          item.NgayApDung ||
          item.NgayBatDau ||
          item.startDate
      );
      const name =
        item.TenMuaVu || item.Ten || item.name || "Mùa vụ chưa đặt tên";
      const nameYear = Number(name.match(/(?:19|20)\d{2}/)?.[0]);
      const branchId = Number(item.ChiNhanh ?? item.branchId);

      return {
        id: item.IDMua ?? item.IDMuaVu ?? item.IDKpi ?? item.ID ?? item.id,
        targetId: item.IDKpi ?? null,
        name,
        year:
          Number(item.Nam ?? item.year) ||
          nameYear ||
          Number(startDate.slice(0, 4)) ||
          null,
        branchId,
        branchName:
          KPI_BRANCH_NAMES[branchId] || `Chi nhánh ${branchId || "?"}`,
        startDate,
        endDate: normalizeDateKey(
          item.KPI_NgayKetThuc || item.NgayKetThuc || item.endDate
        ),
        totalKpi: toNonNegativeNumber(item.KPIMua ?? item.Target),
        phaseCount: Number(item.SoPhase ?? item.phaseCount ?? 0),
      };
    })
    .filter((item) => item.id !== undefined && item.id !== null)
    .sort(
      (first, second) =>
        (second.year || 0) - (first.year || 0) ||
        second.startDate.localeCompare(first.startDate)
    );
};

export const normalizePhaseList = (payload) => {
  const rows = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : [];

  return rows
    .map((item, index) => ({
      id: item.IDPhase ?? item.ID ?? item.id,
      number: Number(item.SoPhase ?? item.Phase ?? item.number ?? index + 1),
      name:
        item.TenPhase ||
        item.Ten ||
        item.name ||
        `Phase ${Number(item.SoPhase ?? item.Phase ?? index + 1)}`,
      startDate: normalizeDateKey(item.NgayBatDau || item.startDate),
      endDate: normalizeDateKey(item.NgayKetThuc || item.endDate),
      totalKpi:
        (item.KPIChiNhanh ?? item.KPI ?? item.KPITong) === null ||
        (item.KPIChiNhanh ?? item.KPI ?? item.KPITong) === undefined ||
        (item.KPIChiNhanh ?? item.KPI ?? item.KPITong) === ""
          ? null
          : toNonNegativeNumber(item.KPIChiNhanh ?? item.KPI ?? item.KPITong),
      totalOriginalKpi: toNonNegativeNumber(item.TongKPIGoc),
      totalActual: toNonNegativeNumber(item.TongThucTe),
      enteredDayCount: Number(item.SoNgayDaNhap || 0),
    }))
    .filter(
      (item) =>
        item.id !== undefined &&
        item.id !== null &&
        item.startDate &&
        item.endDate &&
        item.startDate <= item.endDate
    )
    .sort(
      (first, second) =>
        first.startDate.localeCompare(second.startDate) ||
        first.number - second.number
    );
};

export const buildPhaseDailyRows = (phase, payload) => {
  const apiPhase = payload?.data?.phase || {};
  const branchStartDate =
    normalizeDateKey(apiPhase.NgayBatDau || apiPhase.startDate) || phase.startDate;
  const branchEndDate =
    normalizeDateKey(apiPhase.NgayKetThuc || apiPhase.endDate) || phase.endDate;
  const savedRows = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data?.days)
      ? payload.data.days
    : Array.isArray(payload?.data?.Data)
      ? payload.data.Data
    : Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload?.Data)
        ? payload.Data
      : [];
  const savedByDate = new Map(
    savedRows
      .map((item) => ({
        date: normalizeDateKey(item.Ngay || item.date),
        originalKpi:
          item.ID === null ||
          item.KPI_Goc === null ||
          item.KPI_Goc === undefined
            ? ""
            : String(item.KPI_Goc),
        percentage:
          item.TyLePhanBo === null || item.TyLePhanBo === undefined
            ? ""
            : String(item.TyLePhanBo),
        actual:
          item.ThucTe === null || item.ThucTe === undefined
            ? null
            : Number(item.ThucTe),
        effectiveKpi:
          item.KPI_ThucSu === null || item.KPI_ThucSu === undefined
            ? null
            : Number(item.KPI_ThucSu),
      }))
      .filter((item) => item.date)
      .map((item) => [item.date, item])
  );

  return generateDateKeys(branchStartDate, branchEndDate).map((date) => ({
    date,
    originalKpi: savedByDate.get(date)?.originalKpi ?? "",
    percentage: savedByDate.get(date)?.percentage ?? "",
    actual: savedByDate.get(date)?.actual ?? null,
    effectiveKpi: savedByDate.get(date)?.effectiveKpi ?? null,
  }));
};

export const distributePercentEqually = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const base = Math.floor((100 / rows.length) * 100) / 100;
  let assigned = 0;

  return rows.map((row, index) => {
    const percentage =
      index === rows.length - 1
        ? Number((100 - assigned).toFixed(2))
        : base;
    assigned = Number((assigned + percentage).toFixed(2));
    return { ...row, percentage: String(percentage) };
  });
};

export const calculateKpiFromPercentage = (totalKpi, percentage) =>
  Math.round(
    (toNonNegativeNumber(totalKpi) * toNonNegativeNumber(percentage)) / 100
  );

export const calculateDistributedKpis = (totalKpi, rows) => {
  const normalizedRows = Array.isArray(rows) ? rows : [];
  const roundedTotal = Math.round(toNonNegativeNumber(totalKpi));
  const percentageTotal = normalizedRows.reduce(
    (sum, row) => sum + toNonNegativeNumber(row.percentage),
    0
  );
  if (Math.abs(percentageTotal - 100) > 0.01) {
    return normalizedRows.map((row) =>
      calculateKpiFromPercentage(roundedTotal, row.percentage)
    );
  }

  const allocations = normalizedRows.map((row, index) => {
    const rawValue =
      (roundedTotal * toNonNegativeNumber(row.percentage)) / 100;
    return {
      index,
      value: Math.floor(rawValue),
      remainder: rawValue - Math.floor(rawValue),
    };
  });
  let remaining =
    roundedTotal - allocations.reduce((sum, item) => sum + item.value, 0);

  allocations
    .slice()
    .sort(
      (first, second) =>
        second.remainder - first.remainder || first.index - second.index
    )
    .forEach((item) => {
      if (remaining <= 0) return;
      allocations[item.index].value += 1;
      remaining -= 1;
    });

  return allocations.map((item) => item.value);
};

export const validatePhaseDailyRows = ({ totalKpi, mode, rows }) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return "Phase chưa có ngày hợp lệ.";
  }

  if (mode === "percentage") {
    if (!(Number(totalKpi) > 0)) {
      return "Cần nhập KPI tổng của phase trước khi phân bổ theo phần trăm.";
    }
    const percentages = rows.map((row) => Number(row.percentage));
    if (
      rows.some((row) => row.percentage === "") ||
      percentages.some((value) => !Number.isFinite(value) || value < 0)
    ) {
      return "Phải nhập phần trăm hợp lệ cho tất cả các ngày.";
    }
    const totalPercentage = percentages.reduce((sum, value) => sum + value, 0);
    if (Math.abs(totalPercentage - 100) > 0.01) {
      return `Tổng tỷ lệ hiện là ${totalPercentage.toFixed(2)}%, cần bằng 100%.`;
    }
    return "";
  }

  const quantities = rows.map((row) => Number(row.originalKpi));
  if (
    rows.some((row) => row.originalKpi === "") ||
    quantities.some((value) => !Number.isFinite(value) || value < 0)
  ) {
    return "Phải nhập số lượng KPI hợp lệ cho tất cả các ngày.";
  }
  return "";
};

export const buildDailyKpiPayload = ({ totalKpi, mode, rows }) => {
  const distributedKpis = calculateDistributedKpis(totalKpi, rows);

  return {
    days: rows.map((row, index) => {
      const originalKpi =
        mode === "percentage"
          ? distributedKpis[index]
          : Number(row.originalKpi);

      return {
        Ngay: row.date,
        KPI_Goc: originalKpi,
        ThucTe: row.actual ?? null,
        KPI_ThucSu: row.effectiveKpi ?? originalKpi,
      };
    }),
  };
};
